import { allows } from "@giftforward/entitlements";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { downloadGiftLinksCsv } from "@/api/campaignBuilder";
import ApplyCreditsRow from "@/components/campaignBuilder/review/ApplyCreditsRow/ApplyCreditsRow";
import ConfirmPayModal from "@/components/campaignBuilder/review/ConfirmPayModal/ConfirmPayModal";
import CostSummaryRow from "@/components/campaignBuilder/review/CostSummaryRow/CostSummaryRow";
import ExpiryAckCheckbox from "@/components/campaignBuilder/review/ExpiryAckCheckbox/ExpiryAckCheckbox";
import FundingModelControl, {
  type FundingModel,
} from "@/components/campaignBuilder/review/FundingModelControl/FundingModelControl";
import LaunchButton from "@/components/campaignBuilder/review/LaunchButton/LaunchButton";
import LaunchSuccessOverlay from "@/components/campaignBuilder/review/LaunchSuccessOverlay/LaunchSuccessOverlay";
import NoRecipientsTip from "@/components/campaignBuilder/review/NoRecipientsTip/NoRecipientsTip";
import PaygRow from "@/components/campaignBuilder/review/PaygRow/PaygRow";
import PaymentMethodSection from "@/components/campaignBuilder/review/PaymentMethodSection/PaymentMethodSection";
import ReviewCard from "@/components/campaignBuilder/review/ReviewCard/ReviewCard";
import {
  CREDIT_UNIT_NOTE,
  DEPOSIT_ROUNDING_TOOLTIP_COPY,
  LAUNCH_DENIED,
  LAUNCH_ERROR,
  PAYG_RATE,
  PAYG_REVERTED_NOTICE,
  PAYG_TOO_SMALL_NOTICE,
  POSTPAID_LIMIT_NOTICE,
} from "@/constants/review";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useCampaignCredits } from "@/hooks/useCampaignCredits";
import { useCampaignPaymentMethod } from "@/hooks/useCampaignPaymentMethod";
import { useLaunchCampaign } from "@/hooks/useLaunchCampaign";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { usePermissions } from "@/hooks/usePermissions";
import { usePostpaidTerms } from "@/hooks/usePostpaidTerms";
import type { PostpaidLaunchInput } from "@/interfaces/review";
import { formatMoney } from "@/lib/campaigns";
import {
  addDaysIso,
  campaignExpiryDays,
  computeCostBreakdown,
  computePostpaidSplit,
  expiryAckText,
  launchBlocker,
  launchBlockerText,
  launchButtonLabel,
  paygSizeEligible,
  termsDate,
} from "@/lib/checkout";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";
import { writeDenialMessage } from "@/utils/saveError-helper";
import { isScheduleDateMissing, isScheduleTooSoon } from "@/utils/scheduleDate-helper";
import { resolveCampaignTimeZone, resolveScheduleTimeZone } from "@/utils/timezone-helper";

/**
 * The sticky right-hand buy box. Mirrors the prototype's `#reviewCheckoutCard`
 * (Campaign-Builder-Master-Prototype-Stage2-Revisions.html, markup
 * ~line 10952-11077) block for block and in the same order:
 *
 *   saved card (top, once on file) → divider →
 *   "Cost Summary": Recipients / Budget per gift / Credits to purchase — what it COSTS →
 *   divider →
 *   "Budget Summary": pay-as-you-give switch → Total campaign budget / Due today (40%) /
 *   Potential additional spend when it is on → apply-all-credits → Credits applied →
 *   Estimated Total Payable — what the client PAYS →
 *   expiry acknowledgment → "Add Payment Method" (until one is on file) →
 *   "Add recipients to continue" → CTA.
 *
 * The second block reads top to bottom as the sum that produces its own last line (Jayanth,
 * 2026-08-26): how you fund it, the budget, what is due, less your credits, payable. Credits
 * applied belongs here rather than in Cost Summary because it is a term in that sum, not a fact
 * about the campaign.
 *
 * The card is rendered HEADERLESS: the Stage 2 revisions collapsed the old
 * "Checkout" card title and its inner "Cost summary" label into the single
 * `.checkout-title-bar` (~line 10972), which sits below the saved card rather
 * than above it — so `ReviewCard` gets no `title` and this owns its heading.
 *
 * That bar reads "Cost Summary", the prototype's wording. DIV-7799 item 4 had asked
 * for "Check Out"; review reversed it (Ivan, 2026-08-14), so the prototype now wins
 * on both wording and layout.
 *
 * Positioning (the two-column grid, `lg:sticky lg:top-24`) belongs to the page
 * that renders this card, not to the card itself — `className` is a pure
 * pass-through for that.
 */
export interface CheckoutCardProps {
  className?: string;
}

const NO_RECIPIENTS_ID = "checkout-no-recipients";

// Universal only — the mandatory multiple-use acknowledgment (story Step 4). Pre-ticked; must stay
// ticked to launch. The ⓘ shows the fuller terms.
const MULTI_USE_TERMS =
  "I accept the multiple-use link terms: one person could redeem repeatedly and use up the campaign budget, and those redemptions aren't refundable.";
const MULTI_USE_TERMS_FULL =
  "A universal link can be redeemed more than once — by different people or the same person — until the redemption limit is reached. A determined person could use up the campaign budget, and redeemed gifts aren't refundable. Only launch a universal link where you're comfortable with that risk.";

export default function CheckoutCard({ className }: CheckoutCardProps) {
  const {
    selectedBudget,
    recipients,
    applyAllCredits,
    setApplyAllCredits,
    payAsYouGive,
    setPayAsYouGive,
    postpaid,
    setPostpaid,
    scheduleMode,
    scheduleDate,
    notificationMode,
    linkToList,
    linkQuantity,
    universalToken,
    rolloverEnabled,
    expiryMode,
    customExpiryDate,
    scheduleTime,
    scheduleTz,
    activationTz,
    selectedCauses,
    submitCampaign,
    saveAllDirty,
    campaignId,
    openModal,
    openPlanModal,
    showToast,
    canChooseWallet,
    fundingWallets,
    fundingGroupId: chosenGroupId,
  } = useCampaignBuilder();

  // What drives pricing and the launch gate: anonymous-links campaigns (manual Path A + universal) mint
  // by quantity (no recipient rows), so the count is the link/redemption quantity. Manual Path B (link to
  // a list) and gfg both gift one per recipient, so the count is the recipient list.
  const isManual = notificationMode === "manual";
  const isUniversal = notificationMode === "universal";
  const anonymousLinks = isAnonymousLinks(notificationMode, linkToList);
  const campaignTimeZone = resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz });
  const campaignQuantity = anonymousLinks ? linkQuantity : recipients.length;
  const { ent } = useMyEntitlements();
  const canPayAsYouGive = !!ent && allows(ent, "campaign_budget", "funding", "auto_topup");
  const { defaultGroupId } = usePermissions();

  // Which group's wallet funds this campaign: the one chosen in the builder on a multi-group plan
  // (saved on the draft), else the caller's default — below that a client IS a single group.
  const fundingGroupId = canChooseWallet ? (chosenGroupId ?? defaultGroupId) : defaultGroupId;
  const navigate = useNavigate();
  const { card } = useCampaignPaymentMethod(fundingGroupId);

  // The group's real balance, treated as 1 credit == 1 USD. Reports 0 while loading or when the
  // caller lacks `view CREDIT`, which hides the credits block rather than flashing a stale number.
  // When this campaign closes. Credits that expire before it cannot pay for a redemption after they
  // are gone, so this is what decides which of the wallet's credits are available to it at all.
  const campaignClosesOn = useMemo(
    () =>
      new Date(
        Date.now() +
          campaignExpiryDays(expiryMode, customExpiryDate, campaignTimeZone) * 24 * 60 * 60 * 1000,
      ).toISOString(),
    [expiryMode, customExpiryDate, campaignTimeZone],
  );
  const { balance: creditsBalance, walletBalance: creditsWalletBalance } = useCampaignCredits(
    fundingGroupId,
    campaignClosesOn,
  );

  // Pay-as-you-give also has a MINIMUM SIZE (50 recipients AND 5,000 credits), and `quoteLaunch`
  // refuses a campaign under either floor. Read here so the switch can answer immediately: the
  // refusal used to arrive as a 422 in the confirm dialog, after a deposit had been priced.
  const paygSizeOk = paygSizeEligible({
    budgetPerRecipient: selectedBudget,
    recipientCount: campaignQuantity,
  });

  // A plan without pay-as-you-give can never bill on deposit, even if the flag was set before a
  // downgrade — and neither can a campaign under the floors, however the flag got set. The server
  // refuses both at launch; this keeps the buy box honest.
  const paygActive = payAsYouGive && canPayAsYouGive && paygSizeOk;

  // Why the switch said no, or why it went back to prepaid on its own. Cleared the moment the
  // campaign clears the floors — leaving it up would scold someone who just added the recipients.
  const [paygNotice, setPaygNotice] = useState<string | null>(null);

  // POSTPAID — offered only to an organisation GFG has approved for it, and priced against the
  // client's own terms. Everything here is a preview: launch re-reads the terms and re-checks the
  // credit limit under its locks.
  const { data: postpaidTerms } = usePostpaidTerms();
  const postpaidEligible = !!postpaidTerms?.eligible;
  // The final invoice is issued `lag` days after close — the organisation's term, not chosen here.
  const finalIssuedIso = addDaysIso(campaignClosesOn, postpaidTerms?.finalInvoiceLagDays ?? 14);
  const postpaidSplit = useMemo(
    () =>
      computePostpaidSplit({
        budgetPerRecipient: selectedBudget,
        recipientCount: campaignQuantity,
        creditsBalance,
        applyAllCredits,
        minUpfrontPc: postpaidTerms?.minUpfrontPc ?? 100,
      }),
    [selectedBudget, campaignQuantity, creditsBalance, applyAllCredits, postpaidTerms],
  );
  // No limit floor: a ceiling that does not fit is refused, never shrunk to fit.
  const postpaidOverLimit =
    postpaidTerms?.headroom != null && postpaidSplit.ceiling > Number(postpaidTerms.headroom);
  const postpaidActive = postpaid && postpaidEligible && !postpaidOverLimit;
  const fundingModel: FundingModel = postpaidActive ? "postpaid" : paygActive ? "payg" : "prepaid";

  const breakdown = useMemo(
    () =>
      computeCostBreakdown({
        budgetPerRecipient: selectedBudget,
        recipientCount: campaignQuantity,
        creditsBalance,
        applyAllCredits,
        payAsYouGive: paygActive,
      }),
    [selectedBudget, campaignQuantity, creditsBalance, applyAllCredits, paygActive],
  );

  // The card this campaign bills against. Chosen once, in the Payment Method block at the top of
  // this column; `null` until the buyer picks, which falls back to the group's default. Held here
  // rather than in that block because the launch call needs it — and campaign-local, so choosing a
  // card for one campaign never rewrites the group's own default.
  const [pickedCardId, setPickedCardId] = useState<number | null>(null);
  const paymentMethodId = pickedCardId ?? card?.id ?? null;
  // Same query the block above runs, so React Query serves it from cache.
  const { data: groupCards } = usePaymentMethods(fundingGroupId);

  // What the wallet must cover before this campaign may open. THE ONLY difference between the two
  // funding models at checkout: prepaid buys the whole campaign now, pay-as-you-give buys the
  // deposit and bills the rest as gifts are redeemed.
  const nominatedCard = groupCards?.find((c) => c.id === paymentMethodId) ?? card;

  // Postpaid decides how the deposit is paid in Confirm & pay — credits, card or invoice — so the
  // buy box asks for nothing up front. It needs a card only when the client's terms require one.
  const fundingRequired = postpaidActive ? 0 : paygActive ? breakdown.deposit : breakdown.subtotal;

  // One gate for the CTA, the acknowledgment below it and the footer sentence. Notably NOT a
  // function of the credits checkbox: applying credits changes what is owed, never what the buy
  // box asks for.
  // Only a single fixed send ("certain") can be too soon or missing — "asap" has no date and
  // "different" is per-recipient, set in Step 2.
  // "Too soon" = the scheduled instant is at most 60 minutes away (or gone), read in the schedule zone.
  const schedulePast =
    scheduleMode === "certain" &&
    isScheduleTooSoon(scheduleDate, scheduleTime, resolveScheduleTimeZone(scheduleTz));
  const scheduleMissing = isScheduleDateMissing(scheduleMode, scheduleDate);

  // Universal only: the "I accept the multiple-use link terms" checkbox. Pre-ticked, and must STAY
  // ticked to launch (unticking disables the CTA, unlike the expiry ack's click-error pattern). Only
  // interactable once a valid payment method is on file — before that it's a dimmed, pre-ticked note.
  const [multiUseAccepted, setMultiUseAccepted] = useState(true);
  const hasCardOnFile = paymentMethodId !== null;

  const blocker = launchBlocker({
    recipientCount: campaignQuantity,
    budgetPerRecipient: breakdown.budgetPerRecipient,
    creditsBalance,
    fundingRequired,
    payAsYouGive: postpaidActive ? !!postpaidTerms?.cardOnFileRequired : paygActive,
    applyExistingCredits: applyAllCredits,
    hasPaymentMethod: paymentMethodId !== null,
    schedulePast,
    scheduleMissing,
  });
  // Universal also requires the multiple-use terms to stay accepted — unticking disables the CTA.
  const ready = blocker === null && (!isUniversal || multiUseAccepted);

  // The sentence under the CTA. Every blocker reaches it except "no recipients", which has its
  // own tip above the button.
  const blockerReason = launchBlockerText(blocker);

  const [ackChecked, setAckChecked] = useState(false);
  const [ackError, setAckError] = useState(false);
  // DIV-7811: publishing a donating campaign with no cause picked is refused with an inline
  // error — same click-triggered pattern as the acknowledgment, never a disabled button.
  const [causeError, setCauseError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  // The launched campaign's id, captured at success. `submitCampaign()` clears the builder's
  // `campaignId` (back to CREATE mode) the instant the launch lands, but the manual success overlay
  // offers its "Download Gift Links" CTA ~10s later — reading the now-empty context id produced a
  // `/campaigns//gift-links.csv` request. Stash it here so the download targets the real campaign.
  const [launchedCampaignId, setLaunchedCampaignId] = useState<number | null>(null);
  const ackInputRef = useRef<HTMLInputElement>(null);

  // A roll-over campaign never donates, so it legitimately publishes with no cause; everyone
  // else must name at least one beneficiary before the confirm dialog opens.
  const causesMissing = !rolloverEnabled && selectedCauses.length === 0;

  // The charge itself. A real mutation against POST /campaigns/launch, so this column reads
  // pending/error state instead of running a timer of its own. Destructured because `mutate`
  // and `reset` are referentially stable while the result object is not.
  const {
    mutate: launchCampaign,
    reset: resetLaunch,
    isPending: launching,
    isError: launchFailed,
    error: launchError,
  } = useLaunchCampaign();

  const handleAckChange = useCallback((next: boolean) => {
    setAckChecked(next);
    if (next) setAckError(false);
  }, []);

  // The acknowledgment row unmounts whenever the order stops being launch-ready, so the
  // error has to go with it — otherwise removing the last recipient and adding them back
  // brings the red state straight back without a fresh CTA click. The prototype clears it
  // in the same place (`updateLaunchButtonState()`, which hides #ackError when !showAck).
  useEffect(() => {
    if (!ready) setAckError(false);
  }, [ready]);

  // Picking a cause (or flipping roll-over on) answers the inline error, so it goes the moment
  // the gap it names is filled — leaving it up would scold a user who just complied.
  useEffect(() => {
    if (!causesMissing) setCauseError(false);
  }, [causesMissing]);

  // An activation the campaign is too small for is REFUSED, not ignored: the switch stays off and
  // says why, in place, at the moment of the click.
  const handlePaygChange = useCallback(
    (next: boolean) => {
      if (next && !paygSizeOk) {
        setPaygNotice(PAYG_TOO_SMALL_NOTICE);
        return;
      }
      setPaygNotice(null);
      setPayAsYouGive(next);
    },
    [paygSizeOk, setPayAsYouGive],
  );

  // BACK TO PREPAID, not merely inert. A campaign can fall under a floor after the switch was
  // thrown — recipients removed in Step 2, a lower tier in Step 1 — and `payAsYouGive` left true
  // would keep `campaign_type = automated` in the next draft save while the buy box priced the
  // whole campaign. The screen and the draft both say prepaid, and the notice says why.
  useEffect(() => {
    if (!payAsYouGive || paygSizeOk) return;
    setPayAsYouGive(false);
    setPaygNotice(PAYG_REVERTED_NOTICE);
  }, [payAsYouGive, paygSizeOk, setPayAsYouGive]);

  // The campaign grew past the floors, so the refusal no longer describes anything.
  useEffect(() => {
    if (paygSizeOk) setPaygNotice(null);
  }, [paygSizeOk]);

  // Postpaid that stopped fitting — the budget grew, or the headroom shrank — goes back to prepaid
  // and says why, for the reason pay-as-you-give does above: the draft must not keep a funding
  // model the buy box has stopped pricing. An organisation no longer eligible goes back quietly.
  useEffect(() => {
    if (!postpaid || !postpaidTerms) return;
    if (!postpaidTerms.eligible) {
      setPostpaid(false);
    } else if (postpaidOverLimit) {
      setPostpaid(false);
      setPaygNotice(POSTPAID_LIMIT_NOTICE);
    }
  }, [postpaid, postpaidTerms, postpaidOverLimit, setPostpaid]);

  // The three-way control. Each choice switches the other two off; a refused one leaves the
  // selection where it was and says why.
  const handleFundingChange = useCallback(
    (next: FundingModel) => {
      if (next === "postpaid") {
        if (postpaidOverLimit) {
          setPaygNotice(POSTPAID_LIMIT_NOTICE);
          return;
        }
        setPaygNotice(null);
        setPayAsYouGive(false);
        setPostpaid(true);
        return;
      }
      setPostpaid(false);
      if (next === "payg") handlePaygChange(true);
      else {
        setPaygNotice(null);
        setPayAsYouGive(false);
      }
    },
    [postpaidOverLimit, setPayAsYouGive, setPostpaid, handlePaygChange],
  );

  // Clicking the CTA unacknowledged is a real click that raises the inline error and pulls focus
  // to the checkbox — it never disables the button (prototype `openCampaignCheckout()` ~line 23985).
  const handleLaunch = useCallback(() => {
    if (!ready) return;
    if (!ackChecked) {
      setAckError(true);
      ackInputRef.current?.focus();
      return;
    }
    // DIV-7811's publish gate: a donating campaign needs somewhere for unused credits to go.
    // The server refuses this too (422); catching it here keeps the confirm dialog honest.
    if (causesMissing) {
      setCauseError(true);
      return;
    }
    // A failure from a previous attempt must not greet the next one — the dialog opens clean.
    resetLaunch();
    setConfirmOpen(true);
  }, [ready, ackChecked, causesMissing, resetLaunch]);

  // Only what Step 4 DECIDES crosses the wire. The campaign row, its contacts and their send dates
  // already exist — `saveAllDirty()` below flushes them first — and the server reads everything
  // structural off the campaign row, so sending it again could only contradict the draft.
  const handleConfirmPay = useCallback(
    async (postpaidCheckout?: PostpaidLaunchInput) => {
      // Step 4's save is the FIRST thing "Send gifts now" does: flush every pending step to the
      // draft so it reflects exactly what's being launched. If that write fails, abort before any
      // payment/launch runs (saveAllDirty surfaces its own error toast).
      const saved = await saveAllDirty();
      if (!saved) return;
      // The draft's id is a string in the builder ("" before the first save). `saveAllDirty` above
      // is what guarantees it exists by now; the guard is for the case where that create failed
      // silently — launching without it is a 403 at `scopeWriteGuard`, not a useful error.
      const draftId = Number(campaignId);
      if (!Number.isInteger(draftId) || draftId <= 0) return;
      launchCampaign(
        {
          campaignId: draftId,
          rollover: rolloverEnabled,
          // A roll-over campaign sends no causes even if some were picked before the switch
          // flipped — its remainder returns to the wallet, nothing is bought.
          causes: rolloverEnabled ? [] : selectedCauses,
          total: breakdown.total,
          dueToday: postpaidActive ? postpaidSplit.dueToday : breakdown.dueToday,
          creditsApplied: postpaidActive ? postpaidSplit.creditsApplied : breakdown.creditsApplied,
          applyExistingCredits: applyAllCredits,
          payAsYouGive: paygActive,
          paymentMethodId,
          postpaid: postpaidActive ? postpaidCheckout : undefined,
        },
        {
          onSuccess: () => {
            // Keep the id before submitCampaign() wipes it — the manual download CTA needs it later.
            setLaunchedCampaignId(draftId);
            setConfirmOpen(false);
            setSuccessOpen(true);
            submitCampaign();
          },
        },
      );
    },
    [
      launchCampaign,
      campaignId,
      breakdown,
      applyAllCredits,
      rolloverEnabled,
      selectedCauses,
      paygActive,
      postpaidActive,
      postpaidSplit,
      paymentMethodId,
      submitCampaign,
      saveAllDirty,
    ],
  );

  const handleGoToDashboard = useCallback(() => {
    setSuccessOpen(false);
    navigate("/dashboard");
  }, [navigate]);

  // Success-page "Download Gift Links" — fetches the CSV through apiClient (JWT-authed) and triggers
  // the browser download, then forwards to the dashboard. The browser download runs independently
  // of this SPA navigation, so moving on doesn't interrupt it. ONLY on success: a failed fetch keeps
  // the terminal overlay up (with a toast) so the user can retry rather than landing on the
  // dashboard with no links.
  const handleDownloadLinks = useCallback(() => {
    // Use the id captured at launch, not the context's — submitCampaign() has already cleared the
    // latter back to "" by the time this terminal overlay offers the download.
    if (launchedCampaignId == null) {
      showToast("We couldn't download your gift links. Please try again.");
      return;
    }
    void downloadGiftLinksCsv(launchedCampaignId)
      .then(() => {
        handleGoToDashboard();
      })
      .catch(() => {
        showToast("We couldn't download your gift links. Please try again.");
      });
  }, [launchedCampaignId, showToast, handleGoToDashboard]);

  // Opens the fullscreen plan modal on top of the builder — it stays mounted underneath,
  // so the campaign being built is never left behind (unlike the old /plan route hop).
  const handlePaygUpgrade = useCallback(() => {
    openModal({
      context: "payg",
      onPrimary: () => openPlanModal("payg"),
    });
  }, [openModal, openPlanModal]);

  return (
    <ReviewCard className={className}>
      {/* TEMPORARY — stands in for subscription until it is built. Delete with the component. */}

      <PaymentMethodSection
        placement="top"
        walletGroupId={fundingGroupId}
        value={pickedCardId}
        onSelect={setPickedCardId}
      />

      <h2 className="m-0 mb-3.5 border-b border-card-border pb-3 font-crimson text-[19px] font-semibold text-rodeo-black">
        Cost Summary
      </h2>

      <CostSummaryRow
        label={isUniversal ? "Redemptions" : anonymousLinks ? "Gift links" : "Recipients"}
        value={String(breakdown.recipientCount)}
      />
      <CostSummaryRow label="Budget per gift" value={formatMoney(breakdown.budgetPerRecipient)} />
      <CostSummaryRow
        label="Credits to purchase"
        note={CREDIT_UNIT_NOTE}
        value={breakdown.creditsToPurchase.toLocaleString()}
        strong
        topRule
      />

      <div className="my-3.5 h-px bg-card-border" />

      {/* ONE headline figure, and which one depends on the funding model: prepaid settles the whole
          campaign now, so the total IS what is due; pay-as-you-give settles a deposit, so the
          deposit is. Showing both put two big numbers on the screen and left the buyer deciding
          which one was the ask.

          The credits opt-in sits directly UNDER that figure in either case — it is the control
          that moves it, and a separate "credits applied" line further down was restating an
          arithmetic the checkbox's own right-hand column already carries (Judd, 2026-08-26). */}
      {postpaidActive ? (
        <>
          {/* Postpaid swaps the totals the way pay-as-you-give does: the budget, the minimum
              deposit (a share of the WHOLE budget), the credits put toward it, then what is
              invoiced after close. Confirm & pay shows the deposit less those credits. */}
          <CostSummaryRow label="Total campaign budget" value={formatMoney(postpaidSplit.budget)} />
          <CostSummaryRow
            label="Due today"
            currency="(USD)"
            note={`${postpaidTerms?.minUpfrontPc ?? 0}% minimum deposit`}
            value={formatMoney(postpaidSplit.deposit)}
            emphasis
            icon={
              postpaidSplit.deposit >
              ((postpaidTerms?.minUpfrontPc ?? 0) / 100) * postpaidSplit.budget ? (
                <Tooltip title={DEPOSIT_ROUNDING_TOOLTIP_COPY} enterTouchDelay={0} arrow>
                  <InfoOutlinedIcon
                    className="ml-1.5 shrink-0 cursor-help align-text-bottom text-graphite"
                    sx={{ fontSize: 15 }}
                    aria-label="Why the deposit is rounded up"
                    role="img"
                    tabIndex={0}
                  />
                </Tooltip>
              ) : undefined
            }
          />
          <ApplyCreditsRow
            balance={creditsBalance}
            walletBalance={creditsWalletBalance}
            applied={postpaidSplit.creditsApplied}
            checked={applyAllCredits}
            onChange={setApplyAllCredits}
          />
          <CostSummaryRow
            label="Invoiced after close"
            note={`Only the gifts your recipients actually claim. Issued ${termsDate(
              finalIssuedIso,
            )}, due ${termsDate(
              addDaysIso(finalIssuedIso, postpaidTerms?.netTermsDays ?? 7),
            )} (Net ${postpaidTerms?.netTermsDays ?? 7}).`}
            value={`up to ${formatMoney(postpaidSplit.ceiling)}`}
          />
        </>
      ) : paygActive ? (
        <>
          <CostSummaryRow label="Total campaign budget" value={formatMoney(breakdown.subtotal)} />
          {/* The deposit is held in whole gifts, so it is usually MORE than the bare percentage —
              a number the buyer would otherwise have to reverse-engineer. The tooltip appears only
              when rounding actually moved it; explaining a rule that did not apply is noise. */}
          <CostSummaryRow
            label="Due today"
            currency="(USD)"
            note={`${Math.round(PAYG_RATE * 100)}% deposit`}
            value={formatMoney(breakdown.dueToday)}
            emphasis
            icon={
              breakdown.deposit > breakdown.subtotal * PAYG_RATE ? (
                <Tooltip title={DEPOSIT_ROUNDING_TOOLTIP_COPY} enterTouchDelay={0} arrow>
                  <InfoOutlinedIcon
                    className="ml-1.5 shrink-0 cursor-help align-text-bottom text-graphite"
                    sx={{ fontSize: 15 }}
                    aria-label="Why the deposit is rounded up"
                    role="img"
                    tabIndex={0}
                  />
                </Tooltip>
              ) : undefined
            }
          />
          <ApplyCreditsRow
            balance={creditsBalance}
            walletBalance={creditsWalletBalance}
            applied={breakdown.creditsApplied}
            checked={applyAllCredits}
            onChange={setApplyAllCredits}
          />
          {/* The rest of the budget — what CAN still be billed, before asking where from. */}
          <CostSummaryRow
            label="Potential additional spend"
            note="Charged to your wallet as gifts are redeemed. We'll draw down any existing credit in your wallet before charging any credit top ups to your card."
            value={formatMoney(breakdown.laterBalance)}
            strong
          />
          {/* And where from: a wallet that already covers the remainder means the card is never
              reached. Worth saying outright — the row above is the only scary number on the
              screen, and leaving the buyer to infer this from a balance elsewhere is what makes
              pay-as-you-give read as an open-ended commitment. */}
          {breakdown.laterBalance > 0 && breakdown.potentialCardSpend <= 0 && (
            <p className="m-0 mt-1 font-lato text-[12.5px] font-semibold leading-[1.4] text-good-sage">
              Your remaining {formatMoney(creditsBalance - breakdown.creditsApplied)} in wallet
              credit covers all of this &mdash; no card top-up expected.
            </p>
          )}
        </>
      ) : (
        <>
          <CostSummaryRow
            label="Estimated Total"
            currency="(USD)"
            note="Includes shipping & taxes"
            value={formatMoney(breakdown.total)}
            emphasis
          />
          <ApplyCreditsRow
            balance={creditsBalance}
            walletBalance={creditsWalletBalance}
            applied={breakdown.creditsApplied}
            checked={applyAllCredits}
            onChange={setApplyAllCredits}
          />
        </>
      )}

      {/* Last, above the payment controls: the funding model is a change to everything above it,
          so it reads as "or fund it differently" rather than as another line of the sum. */}
      {/* The three-way choice only where it is real — an organisation approved for postpaid.
          Everyone else keeps the pay-as-you-give switch exactly as it was. */}
      {postpaidEligible ? (
        <FundingModelControl
          value={fundingModel}
          onChange={handleFundingChange}
          paygLocked={!canPayAsYouGive}
          onUpgrade={handlePaygUpgrade}
          // Over the limit, Postpaid stays visible but cannot be picked, and the notice says why —
          // before the click, not after it.
          disabled={postpaidOverLimit ? ["postpaid"] : []}
          notice={paygNotice ?? (postpaidOverLimit ? POSTPAID_LIMIT_NOTICE : null)}
        />
      ) : (
        <PaygRow
          checked={paygActive}
          locked={!canPayAsYouGive}
          notice={paygNotice}
          onChange={handlePaygChange}
          onUpgrade={handlePaygUpgrade}
        />
      )}

      {/* The acknowledgment only appears once the order is ready for that final confirm, keeping
          the buy box compact beforehand (prototype `updateLaunchButtonState()` ~line 23902). */}
      {ready && (
        <ExpiryAckCheckbox
          checked={ackChecked}
          onChange={handleAckChange}
          error={ackError}
          text={expiryAckText({
            rolloverEnabled,
            expiryMode,
            customExpiryDate,
            timeZone: campaignTimeZone,
          })}
          inputRef={ackInputRef}
        />
      )}

      {/* The only funding control left: a card must exist before launch can buy anything. */}
      <PaymentMethodSection placement="bottom" walletGroupId={fundingGroupId} />

      <NoRecipientsTip id={NO_RECIPIENTS_ID} />

      {/* Universal only — the mandatory multiple-use acknowledgment. Pre-ticked; unticking disables the
          CTA (via `ready`). Only interactable once a card is on file; a dimmed pre-ticked note before. */}
      {isUniversal && (
        <label
          className={`mt-4 flex items-start gap-2.5 ${
            hasCardOnFile ? "cursor-pointer" : "opacity-55 cursor-not-allowed"
          }`}
        >
          <input
            type="checkbox"
            checked={multiUseAccepted}
            disabled={!hasCardOnFile}
            onChange={(e) => setMultiUseAccepted(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-dark-dust"
          />
          <span className="font-lato text-[13px] text-graphite leading-relaxed">
            {MULTI_USE_TERMS}
            <Tooltip title={MULTI_USE_TERMS_FULL} enterTouchDelay={0} arrow>
              <InfoOutlinedIcon
                className="ml-1 align-text-bottom cursor-help text-graphite"
                sx={{ fontSize: 15 }}
                aria-label="Full multiple-use link terms"
                role="img"
                tabIndex={0}
              />
            </Tooltip>
          </span>
        </label>
      )}

      {/* DIV-7811 — raised by the CTA when a donating campaign has no beneficiary picked. */}
      {causeError && (
        <p
          role="alert"
          className="mt-4 mb-0 text-center font-lato text-[13.5px] font-semibold text-good-wine"
        >
          Please select at least one cause before publishing.
        </p>
      )}

      <LaunchButton
        // Self-send dispatches nothing — manual gets a CSV, universal gets ONE public link — so the
        // send/schedule wording would promise a send that never happens.
        label={
          isUniversal
            ? "Launch Universal Link"
            : isManual
              ? "Launch Campaign"
              : launchButtonLabel(scheduleMode === "asap")
        }
        ready={ready}
        // "No recipients" is answered by its own tip above the button, not by a sentence below it.
        reasonId={blocker === "no_recipients" ? NO_RECIPIENTS_ID : undefined}
        reason={blockerReason}
        onLaunch={handleLaunch}
      />

      <ConfirmPayModal
        open={confirmOpen}
        dueToday={breakdown.dueToday}
        potentialCardSpend={breakdown.potentialCardSpend}
        total={breakdown.total}
        payAsYouGive={paygActive}
        card={nominatedCard}
        creditsToBuy={breakdown.creditsToBuy}
        processing={launching}
        // The server's own refusal sentence when there is one — "requested 2,500 credits, only
        // 1,400 available" is actionable; a 401/403 becomes the access line (never the raw "out of
        // scope" body); anything else falls to the generic payment copy.
        errorMessage={
          launchFailed
            ? writeDenialMessage(launchError, { denied: LAUNCH_DENIED, fallback: LAUNCH_ERROR })
            : null
        }
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmPay}
        postpaid={
          postpaidActive && postpaidTerms
            ? {
                split: postpaidSplit,
                minUpfrontPc: postpaidTerms.minUpfrontPc,
                perGift: Number(selectedBudget) || 0,
                closesOn: campaignClosesOn,
                netTermsDays: postpaidTerms.netTermsDays,
                finalInvoiceLagDays: postpaidTerms.finalInvoiceLagDays,
                finalPaymentMethod: postpaidTerms.finalPaymentMethod,
                fundingGroupId,
                fundingGroupName:
                  fundingWallets.find((w) => w.groupId === fundingGroupId)?.name ?? null,
              }
            : undefined
        }
      />

      <LaunchSuccessOverlay
        open={successOpen}
        isManual={isManual}
        onGoToDashboard={handleGoToDashboard}
        onDownloadLinks={isManual ? handleDownloadLinks : undefined}
        universalLink={
          isUniversal && universalToken
            ? `${window.location.origin}/u/${universalToken}`
            : undefined
        }
      />
    </ReviewCard>
  );
}
